import React, { useState, useEffect } from 'react';
import { Mail, Users, Send, BarChart3, Plus, Eye, Trash2, Calendar, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMarketing = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Subscriber Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);

  // Newsletter Generator State
  const [contentType, setContentType] = useState('product_updates');
  const [topic, setTopic] = useState('');
  const [bulletPoints, setBulletPoints] = useState(['', '', '']);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    loadData();
  }, []);
  
  // Filter subscribers when search query or subscribers change
  useEffect(() => {
    if (!searchQuery) {
      setFilteredSubscribers(subscribers);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredSubscribers(
        subscribers.filter(sub => 
          sub.email.toLowerCase().includes(query) ||
          (sub.name && sub.name.toLowerCase().includes(query)) ||
          sub.status.toLowerCase().includes(query) ||
          sub.source.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, subscribers]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, campaignsRes, subscribersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/newsletter/analytics/overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/newsletter/campaigns`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/newsletter/subscribers`, { headers })
      ]);

      setOverview(overviewRes.data);
      setCampaigns(campaignsRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    }
  };
  
  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Möchten Sie ${email} wirklich aus dem Newsletter entfernen?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Abonnent erfolgreich gelöscht');
      loadData(); // Reload data
    } catch (error) {
      toast.error('Fehler beim Löschen des Abonnenten');
    }
  };

  const handleGenerateNewsletter = async () => {
    if (!topic.trim()) {
      toast.error('Bitte geben Sie ein Thema ein');
      return;
    }

    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/newsletter/generate`,
        {
          content_type: contentType,
          topic,
          bullet_points: bulletPoints.filter(p => p.trim())
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGeneratedContent(response.data);
      setPreviewHtml(response.data.content_html);
      toast.success('Newsletter erfolgreich generiert!');
    } catch (error) {
      toast.error('Fehler bei der Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (!generatedContent) {
      toast.error('Bitte generieren Sie zuerst einen Newsletter');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/newsletter/campaigns`,
        {
          subject: generatedContent.subject,
          content_type: contentType,
          content_html: generatedContent.content_html,
          content_text: generatedContent.content_text
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Campaign gespeichert!');
      setGeneratedContent(null);
      setTopic('');
      setBulletPoints(['', '', '']);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSendCampaign = async (campaignId, sendType = 'immediate') => {
    if (!confirm(`Newsletter wirklich ${sendType === 'immediate' ? 'sofort' : 'geplant'} versenden?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/newsletter/campaigns/${campaignId}/send`,
        { campaign_id: campaignId, send_type: sendType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Newsletter wird versendet!');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Versenden');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!confirm('Campaign wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/newsletter/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Campaign gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="p-6" style={{ backgroundColor: '#0B0F14', minHeight: '100vh', color: '#E6EDF3' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1EA0E6' }}>
            <Mail className="inline-block mr-2 mb-1" />
            Marketing & Newsletter
          </h1>
          <p className="text-gray-400">Verwalten Sie Ihre Newsletter-Kampagnen und Abonnenten</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {[
            { id: 'overview', label: 'Übersicht', icon: BarChart3 },
            { id: 'create', label: 'Newsletter erstellen', icon: Plus },
            { id: 'campaigns', label: 'Kampagnen', icon: Send },
            { id: 'subscribers', label: 'Abonnenten', icon: Users }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-400 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-sm text-gray-400">Abonnenten</p>
                  <p className="text-2xl font-bold">{overview.active_subscribers}</p>
                  <p className="text-xs text-gray-500">von {overview.total_subscribers} gesamt</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <div className="flex items-center gap-3">
                <Send className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-sm text-gray-400">Versendet</p>
                  <p className="text-2xl font-bold">{overview.sent_campaigns}</p>
                  <p className="text-xs text-gray-500">von {overview.total_campaigns} gesamt</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <div className="flex items-center gap-3">
                <Eye className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-sm text-gray-400">Öffnungsrate</p>
                  <p className="text-2xl font-bold">{overview.avg_open_rate.toFixed(1)}%</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-orange-400" />
                <div>
                  <p className="text-sm text-gray-400">Klickrate</p>
                  <p className="text-2xl font-bold">{overview.avg_click_rate.toFixed(1)}%</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-400" />
                KI-Newsletter Generator
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Content-Typ</label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <option value="product_updates">Produkt-Updates</option>
                    <option value="automotive_tips">Automotive-Tipps</option>
                    <option value="offers">Angebote & Rabatte</option>
                    <option value="mixed">Gemischter Content</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Thema *</label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="z.B. Neue Dual-AI Diagnose Feature"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Stichpunkte (optional)</label>
                  {bulletPoints.map((point, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={point}
                      onChange={(e) => {
                        const newPoints = [...bulletPoints];
                        newPoints[idx] = e.target.value;
                        setBulletPoints(newPoints);
                      }}
                      placeholder={`Stichpunkt ${idx + 1}`}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg mb-2"
                    />
                  ))}
                </div>

                <Button
                  onClick={handleGenerateNewsletter}
                  disabled={isGenerating}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-400"
                >
                  {isGenerating ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generiere...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Newsletter generieren
                    </span>
                  )}
                </Button>

                {generatedContent && (
                  <div className="pt-4 border-t border-white/10 space-y-3">
                    <div className="flex gap-2">
                      <Button onClick={handleSaveCampaign} className="flex-1 bg-green-600 hover:bg-green-700">
                        Als Entwurf speichern
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-[#1A1F26] border-white/10">
              <h2 className="text-xl font-bold mb-4">Vorschau</h2>
              {previewHtml ? (
                <div className="border border-white/10 rounded-lg p-4 bg-white text-black overflow-auto max-h-[600px]">
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  Generieren Sie einen Newsletter, um die Vorschau zu sehen
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-4">
            {campaigns.length === 0 ? (
              <Card className="p-12 bg-[#1A1F26] border-white/10 text-center">
                <Mail className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">Keine Kampagnen vorhanden</p>
              </Card>
            ) : (
              campaigns.map(campaign => (
                <Card key={campaign.id} className="p-6 bg-[#1A1F26] border-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-1">{campaign.subject}</h3>
                      <p className="text-sm text-gray-400 mb-3">
                        Status: <span className={`px-2 py-1 rounded text-xs ${
                          campaign.status === 'sent' ? 'bg-green-900/30 text-green-400' :
                          campaign.status === 'scheduled' ? 'bg-blue-900/30 text-blue-400' :
                          'bg-gray-700/30 text-gray-400'
                        }`}>{campaign.status}</span>
                      </p>
                      {campaign.status === 'sent' && (
                        <div className="flex gap-4 text-sm text-gray-400">
                          <span>📧 {campaign.recipient_count} Empfänger</span>
                          <span>👁️ {campaign.opened_count} geöffnet</span>
                          <span>🖱️ {campaign.clicked_count} geklickt</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {campaign.status === 'draft' && (
                        <>
                          <Button
                            onClick={() => handleSendCampaign(campaign.id, 'immediate')}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <Card className="p-6 bg-[#1A1F26] border-white/10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Abonnenten ({filteredSubscribers.length} {searchQuery && `von ${subscribers.length}`})</h2>
              
              {/* Search Bar */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Suche nach E-Mail, Name, Status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="px-4 py-2 pr-10 rounded-lg bg-[#0D1117] border border-white/10 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-[#1EA7DC] transition-colors"
                    style={{ minWidth: '300px' }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20 bg-white/5">
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">E-Mail</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">Quelle</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">Angemeldet</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-300 uppercase tracking-wider">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-400">
                        {searchQuery ? `Keine Abonnenten gefunden für "${searchQuery}"` : 'Keine Abonnenten vorhanden'}
                      </td>
                    </tr>
                  ) : (
                    filteredSubscribers.map(sub => (
                      <tr key={sub.email} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-3 text-sm text-gray-200 font-medium">{sub.email}</td>
                        <td className="p-3 text-sm text-gray-300">{sub.name || '-'}</td>
                        <td className="p-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sub.status === 'active' ? 'bg-green-900/30 text-green-400' :
                            sub.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                            'bg-gray-700/30 text-gray-400'
                          }`}>{sub.status}</span>
                        </td>
                        <td className="p-3 text-sm text-gray-300">{sub.source}</td>
                        <td className="p-3 text-sm text-gray-300">
                          {new Date(sub.subscribed_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => handleDeleteSubscriber(sub.email)}
                            className="text-red-400 hover:text-red-300 transition-colors p-2 hover:bg-red-900/20 rounded"
                            title="Abonnent löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminMarketing;
